import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useUILibContainer } from '@src/index/context';
import { createPortal } from 'react-dom';

import RoundCloseButton from './RoundCloseButton/RoundCloseButton';
import {
  Background,
  Foreground,
  ContentWrapper,
  ForegroundWrapper,
  ModalWrapper,
  ExternalCloseButton,
  ModalWrapperWithScrollbar,
} from './styles';
import { defaultTheme } from './theme';
import { ModalProps, ModalState } from './types';

/**
 * The Modal is used to display a virtual window to the user that will be the focus of the page.
 * It is positioned above everything. When open, there is a dark backdrop.
 */
const Modal: React.FC<ModalProps> = ({
  width = '408px',
  height = 'unset',
  externalCloseButton = true,
  blurBackground = true,
  className,
  onClose,
  left,
  top,
  bottom,
  right,
  position,
  hasBackground = true,
  enableEscape = true,
  isYoutubeVideo = false,
  ...props
}) => {
  /* state.isOpen determines whether modal is rendered or not, not if it finished opening */
  const [state, setState] = useState<ModalState>({
    isOpen: props.isOpen,
    closing: false,
  });
  const { getModalContainer } = useUILibContainer();
  const ContentWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.isOpen && ContentWrapperRef.current) {
      ContentWrapperRef.current.focus();
    }
  }, [state.isOpen]);

  const disableScrollingInBackground = useCallback((): void => {
    if (!props.enableBackgroundScrolling) {
      document.body.style.overflow = 'hidden';
    }
  }, [props.enableBackgroundScrolling]);

  const enableScrollingInBackground = useCallback((): void => {
    document.body.style.overflow = 'unset';
  }, []);

  const handleClose = useCallback((): void => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleCloseOnEscape = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && props.isOpen) {
        handleClose();
      }
    },
    [handleClose, props.isOpen]
  );

  const onEndTransition = useCallback((): void => {
    setState((prev) => {
      if (prev.closing) {
        return { closing: false, isOpen: false };
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (enableEscape) {
      document.addEventListener('keydown', handleCloseOnEscape);
    }
    return (): void => {
      document.removeEventListener('keydown', handleCloseOnEscape);
    };
  }, [handleCloseOnEscape, enableEscape]);

  useEffect(() => {
    if (props.isOpen && state.isOpen) {
      return;
    }
    if (!props.isOpen && (state.closing || !state.isOpen)) {
      return;
    }

    if (props.isOpen) {
      setState({
        isOpen: true,
        closing: false,
      });
      disableScrollingInBackground();
    } else if (state.isOpen) {
      setState({
        isOpen: true,
        closing: true,
      });

      // This is a fix for when the onTransitionEnd event is not triggered for some reason
      // this was occurring during sign-up in presentation mode - very weird but spent enough time on it
      // that its not worth it to investigate further at this time
      setTimeout(() => {
        onEndTransition();
      }, 1000);

      enableScrollingInBackground();
    }
  }, [
    props.isOpen,
    state,
    disableScrollingInBackground,
    enableScrollingInBackground,
    onEndTransition,
  ]);

  const rootContainer = getModalContainer();
  if (!state.isOpen || !rootContainer) {
    return null;
  }

  const theme = { ...defaultTheme, ...props.theme };

  return createPortal(
    <>
      {hasBackground && (
        <Background
          blurBackground={blurBackground}
          className={className}
          data-modal-classname={className ?? ''}
          data-testid="modal--background"
          disableClose={!onClose}
          externalCloseButton={externalCloseButton}
          state={state}
          theme={theme}
          onClick={handleClose}
        />
      )}
      <ForegroundWrapper
        className={className}
        data-modal-classname={className ?? ''}
        theme={theme}
      >
        <Foreground
          bottom={bottom}
          data-testid="modal--foreground"
          left={left}
          position={position}
          right={right}
          state={state}
          stretchContent={!!props.stretchContent}
          top={top}
          onAnimationEnd={onEndTransition}
          onTransitionEnd={onEndTransition}
        >
          {onClose && externalCloseButton && (
            <ExternalCloseButton isYoutubeVideo={isYoutubeVideo}>
              <RoundCloseButton onClick={onClose} />
            </ExternalCloseButton>
          )}
          {props.consistentVerticalScrollbar ? (
            <ModalWrapperWithScrollbar
              height={height}
              noContentBackground={props.noContentBackground}
              noOverflowX={props.noOverflowX}
              noShadow={props.noShadow}
              width={width}
            >
              <ContentWrapper height={props.contentWrapperHeight || height}>
                {props.children}
              </ContentWrapper>
            </ModalWrapperWithScrollbar>
          ) : (
            <ModalWrapper
              height={height}
              hideScrollbar={props.hideScrollbar}
              noBorderRadius={props.noBorderRadius}
              noContentBackground={props.noContentBackground}
              noOverflow={props.noOverflow}
              noOverflowX={props.noOverflowX}
              noShadow={props.noShadow}
              width={width}
            >
              <ContentWrapper
                ref={ContentWrapperRef}
                data-testid="modal--content"
                height={
                  props.contentWrapperHeight
                    ? props.contentWrapperHeight
                    : props.noOverflow
                      ? '100%'
                      : height
                }
                noContentBackground={props.noContentBackground}
              >
                {props.children}
              </ContentWrapper>
            </ModalWrapper>
          )}
        </Foreground>
      </ForegroundWrapper>
    </>,
    rootContainer
  );
};

export default React.memo(Modal);
